import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doctorsAPI, ratingsAPI } from '../api';
import toast from 'react-hot-toast';
import {
    ArrowLeft,
    Award,
    GraduationCap,
    Briefcase,
    Clock,
    CalendarCheck,
    Star,
    Trash2,
} from 'lucide-react';

export default function DoctorDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [doctor, setDoctor] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [ratings, setRatings] = useState([]);
    const [ratingsLoading, setRatingsLoading] = useState(true);
    const [deleting, setDeleting] = useState(null);

    const StatusBadge = ({ status }) => {
        const map = {
            Accepted: 'badge-success',
            Completed: 'badge-success',
            Pending: 'badge-warning',
            Cancelled: 'badge-danger',
        };
        return (
            <span className={`badge ${map[status] || 'badge-gray'}`}>{status}</span>
        );
    };

    useEffect(() => {
        doctorsAPI
            .getById(id)
            .then((res) => setDoctor(res.data.data))
            .catch(() => toast.error('Failed to load doctor'))
            .finally(() => setLoading(false));

        doctorsAPI
            .getAppointments(id)
            .then((res) => setAppointments(res.data.data))
            .catch(() => toast.error('Failed to load doctor'))
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => {
        setRatingsLoading(true);
        ratingsAPI
            .getAll({ doctor_id: id, limit: 100 })
            .then((res) => setRatings(res.data.data.ratings))
            .catch(() => toast.error('Failed to load ratings'))
            .finally(() => setRatingsLoading(false));
    }, [id]);

    const handleDeleteRating = (ratingId) => {
        if (window.confirm('Are you sure you want to delete this rating?')) {
            setDeleting(ratingId);
            ratingsAPI
                .delete(ratingId)
                .then(() => {
                    setRatings(ratings.filter((r) => r.rating_id !== ratingId));
                    toast.success('Rating deleted');
                })
                .catch(() => toast.error('Failed to delete rating'))
                .finally(() => setDeleting(null));
        }
    };

    const RatingStars = ({ rating }) => {
        return (
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                {[...Array(5)].map((_, i) => (
                    <Star
                        key={i}
                        size={16}
                        style={{
                            fill:
                                i < rating ? 'var(--warning)' : 'var(--text3)',
                            color:
                                i < rating ? 'var(--warning)' : 'var(--text3)',
                        }}
                    />
                ))}
                <span
                    style={{
                        marginLeft: 8,
                        fontWeight: 600,
                        color: 'var(--text)',
                    }}
                >
                    {rating}/5
                </span>
            </div>
        );
    };

    const handleApprove = () => {
        if (!window.confirm('Approve this doctor?')) return;

        doctorsAPI.approve(id)
            .then(() => {
                toast.success('Doctor approved');
                setDoctor({ ...doctor, is_approved: 'Yes' });
            })
            .catch(() => toast.error('Failed to approve'));
    };

    const handleReject = () => {
        if (!window.confirm('Reject this doctor?')) return;

        doctorsAPI.reject(id)
            .then(() => {
                toast.success('Doctor rejected');
                setDoctor({ ...doctor, is_approved: 'No' });
            })
            .catch(() => toast.error('Failed to reject'));
    };

    if (loading)
        return (
            <div className="loading-container">
                <div className="spinner" />
            </div>
        );
    if (!doctor) return <div>Doctor not found</div>;

    const statusColor =
        { Active: '#10b981', Inactive: '#f59e0b', Suspended: '#ef4444' }[
        doctor.status
        ] || '#64748b';

    return (
        <div className="fade-in">
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: 28,
                }}
            >
                <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigate('/doctors')}
                >
                    <ArrowLeft size={14} /> Back
                </button>
                <h1 className="page-title" style={{ fontSize: 22 }}>
                    Doctor Profile
                </h1>
            </div>

            <div className="grid-2">
                {/* Profile Card */}
                <div className="card" style={{ gridColumn: '1 / -1' }}>
                    <div
                        style={{
                            display: 'flex',
                            gap: 24,
                            alignItems: 'flex-start',
                        }}
                    >
                        <div
                            style={{
                                width: 80,
                                height: 80,
                                borderRadius: 20,
                                background:
                                    'linear-gradient(135deg, var(--accent), var(--accent2))',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 28,
                                fontWeight: 700,
                                color: 'white',
                                flexShrink: 0,
                            }}
                        >
                            {doctor.full_name[0]}
                        </div>
                        <div style={{ flex: 1 }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    marginBottom: 6,
                                }}
                            >
                                <h2
                                    style={{
                                        fontFamily: 'Syne',
                                        fontSize: 22,
                                        fontWeight: 700,
                                    }}
                                >
                                    {doctor.full_name}
                                </h2>
                                <div
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '4px 12px',
                                        borderRadius: 20,
                                        background: `${statusColor}18`,
                                        color: statusColor,
                                        fontSize: 12,
                                        fontWeight: 500,
                                    }}
                                >
                                    <div
                                        style={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            background: statusColor,
                                        }}
                                    />
                                    {doctor.status}
                                </div>

                                {doctor.is_approved === 'No' &&
                                    <div style={{ marginTop: 0 }}>
                                        <span
                                            style={{
                                                padding: '4px 10px',
                                                borderRadius: 20,
                                                fontSize: 12,
                                                background:
                                                    doctor.is_approved === 'Yes'
                                                        ? '#10b98120'
                                                        : '#f59e0b20',
                                                color:
                                                    doctor.is_approved === 'Yes'
                                                        ? '#10b981'
                                                        : '#f59e0b',
                                            }}
                                        >
                                            {doctor.is_approved === 'Yes' ? 'Approved' : 'Pending Approval'}
                                        </span>
                                    </div>
                                }
                            </div>
                            <p
                                style={{
                                    color: 'var(--text2)',
                                    marginBottom: 6,
                                }}
                            >
                                {doctor.specialization} • {doctor.education}
                            </p>
                            <p style={{ fontSize: 13, color: 'var(--text3)' }}>
                                {doctor.profile_id}
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            {doctor.is_approved === 'No' && (
                                <>
                                    <button
                                        className="btn btn-success btn-sm"
                                        onClick={handleApprove}
                                    >
                                        Approve
                                    </button>

                                    <button
                                        className="btn btn-danger btn-sm"
                                        onClick={handleReject}
                                    >
                                        Reject
                                    </button>
                                </>
                            )}

                            {doctor.is_approved === 'Yes' && (
                                <span
                                    style={{
                                        padding: '4px 10px',
                                        borderRadius: 20,
                                        fontSize: 12,
                                        background:
                                            doctor.is_approved === 'Yes'
                                                ? '#10b98120'
                                                : '#f59e0b20',
                                        color:
                                            doctor.is_approved === 'Yes'
                                                ? '#10b981'
                                                : '#f59e0b',
                                    }}
                                >
                                    {doctor.is_approved === 'Yes' ? 'Approved' : 'Pending Approval'}
                                </span>
                            )}
                        </div>
                        {/* <div style={{ display: 'flex', gap: 12 }}>
                            <div
                                style={{
                                    textAlign: 'center',
                                    padding: '12px 20px',
                                    background: 'var(--surface2)',
                                    borderRadius: 12,
                                    border: '1px solid var(--border)',
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 20,
                                        fontWeight: 700,
                                        fontFamily: 'Syne',
                                    }}
                                >
                                    ₹{doctor.online_fees}
                                </div>
                                <div
                                    style={{
                                        fontSize: 11,
                                        color: 'var(--text3)',
                                        marginTop: 2,
                                    }}
                                >
                                    Online Fee
                                </div>
                            </div>
                            <div
                                style={{
                                    textAlign: 'center',
                                    padding: '12px 20px',
                                    background: 'var(--surface2)',
                                    borderRadius: 12,
                                    border: '1px solid var(--border)',
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 20,
                                        fontWeight: 700,
                                        fontFamily: 'Syne',
                                    }}
                                >
                                    ₹{doctor.home_fees}
                                </div>
                                <div
                                    style={{
                                        fontSize: 11,
                                        color: 'var(--text3)',
                                        marginTop: 2,
                                    }}
                                >
                                    Home Fee
                                </div>
                            </div>
                        </div> */}
                    </div>


                </div>

                {/* Basic Info */}
                <div className="card">
                    <h3
                        className="detail-section"
                        style={{
                            fontFamily: 'Syne',
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'var(--text1)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            marginBottom: 16,
                        }}
                    >
                        Contact & Basic Info
                    </h3>
                    {[
                        ['Email', doctor.email],
                        [
                            'Mobile',
                            `${doctor.country_code} ${doctor.mobile_number}`,
                        ],
                        ['Country', doctor.country_name],
                        ['Age', doctor.age],
                        ['Gender', doctor.gender],
                        [
                            'Verified',
                            doctor.is_verified === 'Yes' ? '✅ Yes' : '❌ No',
                        ],
                        [
                            'Is Online',
                            doctor.is_online === 'Yes' ? '🟢 Yes' : '⚫ No',
                        ],
                        [
                            'Joined',
                            new Date(doctor.created_at).toLocaleDateString(),
                        ],
                    ].map(([label, val]) => (
                        <div className="detail-row" key={label}>
                            <span className="detail-label">{label}</span>
                            <span className="detail-value">{val || '—'}</span>
                        </div>
                    ))}
                </div>

                <div className="card">
                    <h3
                        className="detail-section"
                        style={{
                            fontFamily: 'Syne',
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'var(--text1)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            marginBottom: 16,
                        }}
                    >
                        Education & Experience
                    </h3>
                    {[
                        ['Education', doctor.education],
                        ['Specialization', doctor.specialization],
                        ['Experience (Years)', doctor.experience_year],
                    ].map(([label, val]) => (
                        <div className="detail-row" key={label}>
                            <span className="detail-label">{label}</span>
                            <span className="detail-value">{val || '—'}</span>
                        </div>
                    ))}
                    <h3
                        className="detail-section"
                        style={{
                            fontFamily: 'Syne',
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'var(--text1)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            marginBottom: 16,
                            marginTop: 20
                        }}
                    >
                        Hospital Information
                    </h3>
                    {[
                        ['Offline Service Available', doctor.available_offline],
                        ['Number Of Beds', doctor.number_of_beds],
                        ['Transport Available', doctor.transport_available],
                        ['ICU & Ventilator Available', doctor.critical_service],
                    ].map(([label, val]) => (
                        <div className="detail-row" key={label}>
                            <span className="detail-label">{label}</span>
                            <span className="detail-value">{val || '—'}</span>
                        </div>
                    ))}
                </div>
                {/* Registration Info */}
                {/*  <div className="card">
                    <h3
                        style={{
                            fontFamily: 'Syne',
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'var(--text3)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            marginBottom: 16,
                        }}
                    >
                        Registration & Verification
                    </h3>
                    {[
                        ['Reg. Year', doctor.registration_year],
                        ['Reg. Number', doctor.registration_number],
                        ['Practice Number', doctor.practice_number],
                        [
                            'Verified',
                            doctor.is_verified === 'Yes' ? '✅ Yes' : '❌ No',
                        ],
                        ['Steps Completed', doctor.steps],
                        [
                            'Joined',
                            new Date(doctor.created_at).toLocaleDateString(),
                        ],
                    ].map(([label, val]) => (
                        <div className="detail-row" key={label}>
                            <span className="detail-label">{label}</span>
                            <span className="detail-value">{val || '—'}</span>
                        </div>
                    ))}
                </div> */}

                {/* About */}
                {doctor.about_doctor && (
                    <div className="card" style={{ gridColumn: '1 / -1' }}>
                        <h3
                            style={{
                                fontFamily: 'Syne',
                                fontSize: 14,
                                fontWeight: 600,
                                color: 'var(--text1)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                marginBottom: 12,
                            }}
                        >
                            About
                        </h3>
                        <p
                            style={{
                                fontSize: 14,
                                color: 'var(--text2)',
                                lineHeight: 1.7,
                            }}
                        >
                            {doctor.about_doctor}
                        </p>
                    </div>
                )}

                {/* Educations */}
                {doctor.educations?.length > 0 && (
                    <div className="card">
                        <h3
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                fontFamily: 'Syne',
                                fontSize: 14,
                                fontWeight: 600,
                                color: 'var(--text3)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                marginBottom: 16,
                            }}
                        >
                            <GraduationCap size={15} /> Education
                        </h3>
                        {doctor.educations.map((e) => (
                            <div
                                key={e.education_id}
                                style={{
                                    padding: '12px 0',
                                    borderBottom: '1px solid var(--border)',
                                    lastChild: { borderBottom: 'none' },
                                }}
                            >
                                <div style={{ fontWeight: 500, fontSize: 14 }}>
                                    {e.degree_name}
                                </div>
                                <div
                                    style={{
                                        fontSize: 13,
                                        color: 'var(--text3)',
                                        marginTop: 2,
                                    }}
                                >
                                    {e.college_name}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Awards */}
                {doctor.awards?.length > 0 && (
                    <div className="card">
                        <h3
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                fontFamily: 'Syne',
                                fontSize: 14,
                                fontWeight: 600,
                                color: 'var(--text3)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                marginBottom: 16,
                            }}
                        >
                            <Award size={15} /> Awards
                        </h3>
                        {doctor.awards.map((a) => (
                            <div
                                key={a.award_id}
                                style={{
                                    padding: '12px 0',
                                    borderBottom: '1px solid var(--border)',
                                }}
                            >
                                <div style={{ fontWeight: 500, fontSize: 14 }}>
                                    {a.award_name}
                                </div>
                                <div
                                    style={{
                                        fontSize: 13,
                                        color: 'var(--text3)',
                                        marginTop: 2,
                                    }}
                                >
                                    {a.award_year}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Work Experiences */}
                {doctor.experiences?.length > 0 && (
                    <div className="card" style={{ gridColumn: '1 / -1' }}>
                        <h3
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                fontFamily: 'Syne',
                                fontSize: 14,
                                fontWeight: 600,
                                color: 'var(--text1)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                marginBottom: 16,
                            }}
                        >
                            <Briefcase size={15} /> Work Experience
                        </h3>
                        {doctor.experiences.map((e) => (
                            <div
                                key={e.experience_id}
                                style={{
                                    padding: '14px 0',
                                    borderBottom: '1px solid var(--border)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                }}
                            >
                                <div>
                                    <div
                                        style={{
                                            fontWeight: 500,
                                            fontSize: 14,
                                        }}
                                    >
                                        {e.hospital_name}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 13,
                                            color: 'var(--text3)',
                                            marginTop: 3,
                                        }}
                                    >
                                        {e.hospital_address}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: 'var(--text3)',
                                            marginTop: 3,
                                        }}
                                    >
                                        {e.employment_type}
                                    </div>
                                </div>
                                <div
                                    style={{
                                        textAlign: 'right',
                                        fontSize: 13,
                                        color: 'var(--text3)',
                                    }}
                                >
                                    <div>
                                        {e.start_date?.slice(0, 7)} —{' '}
                                        {e.is_currently_working === 'Yes'
                                            ? 'Present'
                                            : e.end_date?.slice(0, 7)}
                                    </div>
                                    <div style={{ marginTop: 4 }}>
                                        {e.years_of_experience} yrs
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Working Hours */}
                {doctor.workingHours?.length > 0 && (
                    <div className="card">
                        <h3
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                fontFamily: 'Syne',
                                fontSize: 14,
                                fontWeight: 600,
                                color: 'var(--text1)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                marginBottom: 16,
                            }}
                        >
                            <Clock size={15} /> Working Hours
                        </h3>
                        {doctor.workingHours.map((w) => (
                            <div key={w.working_hour_id} className="detail-row">
                                <span className="detail-label">
                                    {w.day_of_week}
                                </span>
                                <span className="detail-value">
                                    {w.is_closed === 'Yes' ? (
                                        <span
                                            style={{ color: 'var(--danger)' }}
                                        >
                                            Closed
                                        </span>
                                    ) : (
                                        `${w.opening_time} – ${w.closing_time}`
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Patient Ratings & Reviews */}
                <div className="card" style={{ gridColumn: '1 / -1' }}>
                    <h3
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            fontFamily: 'Syne',
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'var(--text1)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            marginBottom: 16,
                        }}
                    >
                        <Star size={15} /> Patient Ratings & Reviews
                    </h3>

                    {ratingsLoading ? (
                        <div className="loading-container">
                            <div className="spinner" />
                        </div>
                    ) : ratings.length === 0 ? (
                        <div
                            style={{
                                textAlign: 'center',
                                color: 'var(--text3)',
                                padding: '40px 20px',
                            }}
                        >
                            No ratings yet
                        </div>
                    ) : (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 14,
                            }}
                        >
                            {ratings.map((r) => (
                                <div
                                    key={r.rating_id}
                                    style={{
                                        padding: 14,
                                        border: '1px solid var(--border)',
                                        borderRadius: 'var(--radius)',
                                        backgroundColor: 'var(--surface)',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            marginBottom: 10,
                                        }}
                                    >
                                        <div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                    marginBottom: 6,
                                                }}
                                            >
                                                <div
                                                    className="avatar"
                                                    style={{
                                                        width: 32,
                                                        height: 32,
                                                        fontSize: 11,
                                                    }}
                                                >
                                                    {(r.patient_name || 'P')[0]}
                                                </div>
                                                <div
                                                    style={{
                                                        fontWeight: 600,
                                                        color: 'var(--text)',
                                                    }}
                                                >
                                                    {r.patient_name || '—'}
                                                </div>
                                            </div>
                                            <div style={{ marginLeft: 42 }}>
                                                <RatingStars
                                                    rating={r.rating}
                                                />
                                            </div>
                                        </div>
                                        <button
                                            onClick={() =>
                                                handleDeleteRating(r.rating_id)
                                            }
                                            disabled={deleting === r.rating_id}
                                            className="btn btn-sm btn-danger"
                                            style={{ gap: 4 }}
                                        >
                                            <Trash2 size={14} /> Delete
                                        </button>
                                    </div>

                                    {r.review && (
                                        <div
                                            style={{
                                                padding: 10,
                                                backgroundColor:
                                                    'var(--surface2)',
                                                borderRadius: 'var(--radius)',
                                                marginTop: 10,
                                                borderLeft:
                                                    '3px solid var(--warning)',
                                            }}
                                        >
                                            <p
                                                style={{
                                                    fontSize: 13,
                                                    color: 'var(--text2)',
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                "{r.review}"
                                            </p>
                                        </div>
                                    )}

                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: 'var(--text3)',
                                            marginTop: 10,
                                        }}
                                    >
                                        {new Date(
                                            r.created_at
                                        ).toLocaleDateString()}{' '}
                                        at{' '}
                                        {new Date(
                                            r.created_at
                                        ).toLocaleTimeString()}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Appointments */}
                <div className="card p-0" style={{ gridColumn: '1 / -1', padding: 0 }}>
                    <h3
                        style={{
                            fontFamily: 'Syne',
                            fontSize: 16,
                            fontWeight: 600,
                            marginBottom: 24,
                            marginInlineStart: 24,
                            marginTop: 24,
                        }}
                    >
                        Appointment History
                    </h3>
                    {appointments?.length === 0 ? (
                        <div className="empty-state">
                            <p>No appointments yet</p>
                        </div>
                    ) : (
                        <div
                            className="table-wrapper"
                            style={{ border: 'none', borderRadius: 0 }}
                        >
                            <table>
                                <thead>
                                    <tr>
                                        <th>Patient</th>
                                        <th>Address</th>
                                        <th>Type</th>
                                        <th>Date & Time</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {appointments.map((a) => (
                                        <tr key={a.appointment_id}>
                                            <td
                                                style={{ color: 'var(--text)' }}
                                            >
                                                {a.patient_name || '—'}
                                            </td>
                                            <td><div
                                                style={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    maxWidth: '400px'
                                                }}
                                            >
                                                {a.patient_address}
                                            </div></td>
                                            <td>
                                                <span className="tag">
                                                    {a.appointment_type}
                                                </span>
                                            </td>
                                            <td>
                                                {a.appointment_date}{' '}
                                                {a.appointment_time}
                                            </td>
                                            <td>
                                                <StatusBadge
                                                    status={a.status}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
